import React, { useEffect, useState } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import Modal from './Modal'
import { FaCar, FaWalking } from "react-icons/fa";
import { API_BASE } from '../config'
import { toUtcIsoString } from '../utils/dateUtils'
import { normalizeGates } from '../utils/gates'
import { getCommunity, searchOwners } from '../services/onboardingService'

export function AddEntryExitModal({ isOpen, onClose, onSaved, communityId }) {
  const [mode, setMode] = useState('VEHICLE');
  const [formData, setFormData] = useState({
    personName: '',
    unit: '',
    gate: '',
    vehicleNumber: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [ownerSearchError, setOwnerSearchError] = useState('');
  const [ownerResults, setOwnerResults] = useState([]);
  const [gates, setGates] = useState([]);
  const [gatesLoading, setGatesLoading] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('VEHICLE');
      setFormData({ personName: '', unit: '', gate: '', vehicleNumber: '', notes: '' });
      setErrors({});
      setLookupError('');
      setOwnerSearchTerm('');
      setOwnerSearchError('');
      setOwnerResults([]);
      setGates([]);
      setGatesLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    let active = true
    async function loadGates() {
      const cid = communityId || sessionStorage.getItem('community_id') || ''
      if (!isOpen || !cid) return
      setGatesLoading(true)
      try {
        const comm = await getCommunity({ communityId: cid })
        if (!active) return
        const normalized = normalizeGates(comm?.GATES || comm?.gates || comm?.GATE_LIST || comm?.gateList || '')
        setGates(normalized)
      } catch (err) {
        console.error('failed to load gates', err)
        if (active) setGates([])
      } finally {
        if (active) setGatesLoading(false)
      }
    }
    loadGates()
    return () => { active = false }
  }, [isOpen, communityId])

  if (!isOpen) return null;

  const decodeEmail = (token) => {
    try {
      const p = token.split('.')[1];
      const json = decodeURIComponent(
        atob(p.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return (JSON.parse(json).email || '').toLowerCase();
    } catch {
      return '';
    }
  };

  const validate = () => {
    const errs = {};
    if (mode === 'VEHICLE' && !formData.vehicleNumber.trim()) {
      errs.vehicleNumber = 'Vehicle number is required';
    }
    if (mode === 'WALK' && !formData.personName.trim()) {
      errs.personName = 'Person name is required';
    }
    if (!formData.gate.trim()) {
      errs.gate = 'Gate is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const applyOwnerSelection = (owner) => {
    const firstName = owner?.FIRST_NAME || owner?.firstName || owner?.FIRST_NM || owner?.first_name || '';
    const lastName = owner?.LAST_NAME || owner?.lastName || owner?.LAST_NM || owner?.last_name || '';
    const unit = owner?.UNIT_NBR || owner?.unitNbr || owner?.unit_nbr || owner?.UNIT || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    setFormData((prev) => ({ ...prev, personName: fullName, unit }));
    setOwnerSearchError('');
    setOwnerResults([]);
    setOwnerSearchTerm(fullName);
  };

  const lookupWalkInOwner = async () => {
    const q = ownerSearchTerm.trim();
    if (!q) {
      setOwnerSearchError('Enter a first name or last name to search');
      return;
    }
    const cid = communityId || sessionStorage.getItem('community_id') || '';
    if (!cid) {
      setOwnerSearchError('Community not available');
      return;
    }
    setOwnerSearchLoading(true);
    setOwnerSearchError('');
    setOwnerResults([]);
    try {
      const idToken = sessionStorage.getItem('id_token');
      const results = await searchOwners({ communityId: cid, query: q }, idToken || undefined);
      if (!Array.isArray(results) || results.length === 0) {
        setOwnerSearchError('No registered owner found. You can still enter the person details manually below.');
        return;
      }
      if (results.length === 1) {
        applyOwnerSelection(results[0]);
        return;
      }
      setOwnerResults(results);
    } catch (err) {
      console.error('owner search failed', err);
      setOwnerSearchError(err?.message || 'Owner search failed');
    } finally {
      setOwnerSearchLoading(false);
    }
  };

  const lookupVehicle = async () => {
    const v = formData.vehicleNumber.trim();
    if (!v) return;
    setLookupLoading(true);
    setLookupError('');
    try {
      const idToken = sessionStorage.getItem('id_token');
      if (!idToken) {
        setLookupError('Not authenticated');
        setLookupLoading(false);
        return;
      }
      const email = decodeEmail(idToken);
      if (!email) {
        setLookupError('Could not resolve email');
        setLookupLoading(false);
        return;
      }
      const regResp = await fetch(
        `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (!regResp.ok) {
        setLookupError('Failed to resolve community');
        setLookupLoading(false);
        return;
      }
      const user = await regResp.json();
      const communityId = user.COMMUNITY_ID || user.communityId || '';
      if (!communityId) {
        setLookupError('Community not found');
        setLookupLoading(false);
        return;
      }

      const resp = await fetch(
        `${API_BASE}/vehicles?communityId=${encodeURIComponent(communityId)}&vehicleNumber=${encodeURIComponent(v)}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (!resp.ok) {
        setLookupError('Vehicle lookup failed');
        setLookupLoading(false);
        return;
      }
      const arr = await resp.json();
      if (!Array.isArray(arr) || arr.length === 0) {
        setLookupError('Vehicle not registered');
        setLookupLoading(false);
        return;
      }
      const rec = arr[0];
      setFormData((prev) => ({
        ...prev,
        personName: rec.OWNER_NAME || rec.ownerName || prev.personName,
        unit: rec.UNIT_NBR || rec.unitNbr || prev.unit,
      }));
      setLookupError('');
    } catch (err) {
      console.error('lookup failed', err);
      setLookupError('Lookup failed');
    }
    setLookupLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const idToken = sessionStorage.getItem('id_token');
      if (!idToken) {
        setErrors({ form: 'Not authenticated' });
        setLoading(false);
        return;
      }

      const payload = {
        MODE: mode,
        GATE: formData.gate.trim(),
        entryTime: toUtcIsoString(new Date()),
      };

      // Include communityId from the logged-in user's registration record
      const cid = communityId || sessionStorage.getItem('community_id') || '';
      if (cid) payload.COMMUNITY_ID = cid;

      if (formData.personName.trim()) {
        payload.PERSON_NAME = formData.personName.trim();
      }
      if (formData.unit.trim()) {
        payload.UNIT_NBR = formData.unit.trim();
      }
      if (formData.notes.trim()) {
        payload.NOTES = formData.notes.trim();
      }
      if (mode === 'VEHICLE' && formData.vehicleNumber.trim()) {
        payload.VEHICLE_NUMBER = formData.vehicleNumber.trim();
      }

      const resp = await fetch(`${API_BASE}/vehicle-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const text = await resp.text();
        setErrors({ form: text || `Error ${resp.status}` });
        setLoading(false);
        return;
      }

      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      console.error('submit failed', err);
      setErrors({ form: 'Submission failed' });
    }
    setLoading(false);
  };

  return (
    <Modal title="Log Entry" onClose={onClose} scrollOnDesktop>
      <form onSubmit={handleSubmit} className="p-4 space-y-4 max-w-full overflow-hidden">
          {/* Mode Toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setMode('VEHICLE')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                mode === 'VEHICLE'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FaCar size={16} /> Vehicle
            </button>
            <button
              type="button"
              onClick={() => setMode('WALK')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                mode === 'WALK'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FaWalking size={16} /> Walk-in
            </button>
          </div>

          {/* Vehicle Number + Lookup (VEHICLE mode only) */}
          {mode === 'VEHICLE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Number <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <input
                  placeholder="e.g. KA01AB1234"
                  value={formData.vehicleNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, vehicleNumber: e.target.value.toUpperCase() })
                  }
                  className="min-w-0 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={lookupVehicle}
                  disabled={lookupLoading || !formData.vehicleNumber.trim()}
                  className="w-full sm:w-auto shrink-0 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                >
                  {lookupLoading ? '…' : 'Lookup'}
                </button>
              </div>
              {errors.vehicleNumber && (
                <p className="text-red-500 text-xs mt-1">{errors.vehicleNumber}</p>
              )}
              {lookupError && (
                <p className="text-amber-600 text-xs mt-1">{lookupError}</p>
              )}
            </div>
          )}

          {mode === 'WALK' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Owner / Resident <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <input
                  placeholder="Enter first name or last name"
                  value={ownerSearchTerm}
                  onChange={(e) => setOwnerSearchTerm(e.target.value)}
                  className="min-w-0 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={lookupWalkInOwner}
                  disabled={ownerSearchLoading || !ownerSearchTerm.trim()}
                  className="w-full sm:w-auto shrink-0 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                >
                  {ownerSearchLoading ? 'Searching…' : 'Search'}
                </button>
              </div>
              {ownerSearchError && (
                <div className="mt-1">
                  <p className="text-amber-600 text-xs">{ownerSearchError}</p>
                  {ownerSearchError.includes('No registered owner found') && (
                    <button
                      type="button"
                      onClick={() => setOwnerSearchError('')}
                      className="mt-1 text-xs font-medium text-blue-600 underline underline-offset-2"
                    >
                      Continue manually
                    </button>
                  )}
                </div>
              )}
              {ownerResults.length > 1 && (
                <div className="mt-2 border border-gray-200 rounded-lg bg-gray-50 max-h-40 overflow-y-auto">
                  {ownerResults.map((owner, idx) => {
                    const firstName = owner?.FIRST_NAME || owner?.firstName || owner?.FIRST_NM || owner?.first_name || '';
                    const lastName = owner?.LAST_NAME || owner?.lastName || owner?.LAST_NM || owner?.last_name || '';
                    const unit = owner?.UNIT_NBR || owner?.unitNbr || owner?.unit_nbr || owner?.UNIT || 'Unit not available';
                    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
                    return (
                      <button
                        key={`${fullName}-${unit}-${idx}`}
                        type="button"
                        onClick={() => applyOwnerSelection(owner)}
                        className="w-full text-left px-3 py-2 border-b border-gray-200 last:border-b-0 hover:bg-blue-50"
                      >
                        <div className="text-sm font-medium text-gray-800">{fullName || 'Unknown owner'}</div>
                        <div className="text-xs text-gray-500">Unit {unit}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Person Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Person Name {mode === 'WALK' && <span className="text-red-500">*</span>}
            </label>
            <input
              placeholder="Full name"
              value={formData.personName}
              onChange={(e) => setFormData({ ...formData, personName: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            {errors.personName && (
              <p className="text-red-500 text-xs mt-1">{errors.personName}</p>
            )}
          </div>

          {/* Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <input
              placeholder="e.g. A-101"
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Gate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gate <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.gate}
              onChange={(e) => setFormData({ ...formData, gate: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              disabled={gatesLoading}
            >
              <option value="">{gatesLoading ? 'Loading gates…' : 'Select gate'}</option>
              {gates.map((gate) => (
                <option key={gate} value={gate}>{gate}</option>
              ))}
              {!gatesLoading && gates.length === 0 && (
                <>
                  <option value="Main Gate">Main Gate</option>
                  <option value="Gate 1">Gate 1</option>
                  <option value="Gate 2">Gate 2</option>
                  <option value="Gate 3">Gate 3</option>
                  <option value="Service Gate">Service Gate</option>
                </>
              )}
            </select>
            {errors.gate && (
              <p className="text-red-500 text-xs mt-1">{errors.gate}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              placeholder="Optional notes…"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>

          {/* Form Error */}
          {errors.form && (
            <p className="text-red-600 text-sm bg-red-50 p-2 rounded-lg">{errors.form}</p>
          )}

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Log Entry'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
